import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { IntegrationExportEntity } from "@nova/constants"
import { AGOLSettings } from "@nova/db-table-integration-authorizations"
import { IntegrationType } from "@nova/schemas"
import { useIntegrationAuthorizations } from "features/data-management/integrations/useIntegrations"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { useExportToAGOL } from "features/data-management/workspace/workspace/body/settings-and-collaborators/integrations/useExportToAGOL"
import { useExportToCalTopo } from "features/data-management/workspace/workspace/body/settings-and-collaborators/integrations/caltopo/useExportToCalTopo"
import { useExportToTAK } from "features/data-management/workspace/workspace/body/settings-and-collaborators/integrations/tak/useExportToTAK"
import { selectActivatedFireId } from "features/map/control/selectors"
import { buildOAuthPopup, isAuthExpired } from "integrations/oauth/oauthHelpers"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { setGlobalNotificationContext } from "user/profile/slice"

export const useIntegrationExportOptions = (
    entities: { id: string; type: IntegrationExportEntity }[]
) => {
    const dispatch = useAppDispatch()
    const workspaceId = useAppSelector(selectActiveWorkspaceId())
    const projectId = useAppSelector(selectActivatedFireId)
    const { isEditor } = useWorkspacePermissions(projectId)

    const { data: agolAuths, invalidateAuthorizationsCache: invalidateAGOL } =
        useIntegrationAuthorizations({
            authType: "oauth",
            integration: IntegrationType.agol,
            workspaceId,
        })
    const { data: takAuths, invalidateAuthorizationsCache: invalidateTAK } =
        useIntegrationAuthorizations({
            authType: "certificate",
            integration: IntegrationType.tak,
            workspaceId,
        })
    const { data: caltopoAuths, invalidateAuthorizationsCache: invalidateCalTopo } =
        useIntegrationAuthorizations({
            authType: "caltopo",
            integration: IntegrationType.caltopo,
            workspaceId,
        })
    const { exportToAGOL } = useExportToAGOL()
    const { exportToTAK } = useExportToTAK()
    const { exportToCalTopo } = useExportToCalTopo()

    if (!isEditor || !workspaceId || !projectId || !entities.length) return []

    const request = { workspaceId, projectId, entities }
    const agolAuth = agolAuths?.at(0)

    return [
        agolAuth && {
            name: (agolAuth.settings as AGOLSettings).selectedWebMap?.title ?? "User Content",
            send: async () => {
                if (isAuthExpired(agolAuth)) {
                    await buildOAuthPopup(IntegrationType.agol, workspaceId)
                    await invalidateAGOL()
                    dispatch(
                        setGlobalNotificationContext({
                            message: `Connected to ${IntegrationType.agol.toUpperCase()}.`,
                            status: GlobalNotificationStatus.success,
                            timeoutInMs: 3000,
                        })
                    )
                }
                await exportToAGOL(request)
                await invalidateAGOL()
            },
        },
        takAuths?.at(0) && {
            name: "TAK",
            send: async () => {
                await exportToTAK(request)
                await invalidateTAK()
            },
        },
        caltopoAuths?.at(0) && {
            name: "CalTopo",
            send: async () => {
                await exportToCalTopo(request)
                await invalidateCalTopo()
            },
        },
    ].filter((option) => !!option)
}
